import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
// Import Shared Utils
import { post } from 'shared-components/src/utils/http';
// Import Global Components
import Button from '../../../components/Button';
// Import Common Utils
import { getInitialStatus, getNewStatus } from '../../../utils/cardStatus';
// Import Local Components
import ConfirmSuspendModal from './ConfirmSuspendModal';

const ModifyStatusButton = ({
  lastFourDigits,
  accountId,
  cardStatus,
  setAlert,
  onStatusChange,
}) => {
  const [status, setStatus] = useState(getInitialStatus(cardStatus));
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [modalError, setModalError] = useState(false);

  // Create intl instance
  const intl = useIntl();

  useEffect(() => {
    setStatus(getInitialStatus(cardStatus));
  }, [cardStatus]);

  // Update card status to either suspended or unsuspended.
  const handleUpdateCardStatus = async status => {
    try {
      setModalLoading(true);
      setModalError(false);

      const body = {
        status,
        last_four_digits: lastFourDigits,
      };

      // Send request to modify card status
      await post(
        `/api/v1/cardholder_portal/cardholder/accounts/${accountId}/modify_status`,
        body
      );

      setModalLoading(false);
      setShowConfirmModal(false);
      setAlert({ for: `${status}-card-success`, type: 'success' });
      setStatus(getNewStatus(status));
      // Let the parent refetch the account so the dashboard status updates
      await onStatusChange();
    } catch (err) {
      setModalLoading(false);
      setModalError(true);
    }
  };

  // Handle button click - show confirmation modal
  const handleButtonClick = () => {
    setModalError(false);
    setShowConfirmModal(true);
  };

  // Handle confirm from modal
  const handleConfirm = () => {
    handleUpdateCardStatus(status);
  };

  // Handle cancel from modal
  const handleCancel = () => {
    setShowConfirmModal(false);
    setModalError(false);
  };

  // If cardStatus is closed, we should disabled the ability to modify card status
  const isClosed = cardStatus === 'closed';

  return (
    <>
      {showConfirmModal && (
        <ConfirmSuspendModal
          status={status}
          loading={modalLoading}
          error={modalError}
          onConfirm={handleConfirm}
          onCancel={handleCancel}
        />
      )}
      <Button
        page="home"
        buttonFor={status}
        type="secondary"
        size="large"
        buttonText={intl.messages[`home-${status}-button`]}
        onClick={handleButtonClick}
        disabled={isClosed}
        title={
          isClosed &&
          'The card on this account is closed. This action is not available'
        }
      />
    </>
  );
};

ModifyStatusButton.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  lastFourDigits: PropTypes.string.isRequired,
  accountId: PropTypes.number.isRequired,
  setAlert: PropTypes.func.isRequired,
  cardStatus: PropTypes.string.isRequired,
  onStatusChange: PropTypes.func,
};

ModifyStatusButton.defaultProps = {
  onStatusChange: () => {},
};

export default ModifyStatusButton;
